/**
 * WeatherSystem — fetches the current weather for the room from the server
 * and renders appropriate visual effects on the GardenScene.
 *
 *   clear      → nothing extra
 *   rain       → falling rain drops across the screen
 *   fog        → soft fog layer drifting across the painting
 *   fireflies  → dense cluster of extra fireflies (3x normal)
 */
const WEATHER_URL = (code) => `https://farm-api.jacobterrell.dev/heirloom/weather/${encodeURIComponent(code)}`;

export class WeatherSystem {
  constructor(scene) {
    this.scene = scene;
    this.current = 'clear';
    this._objects = [];
  }

  async load(roomCode) {
    try {
      const res = await fetch(WEATHER_URL(roomCode));
      if (!res.ok) return 'clear';
      const json = await res.json();
      this.current = json?.weather || 'clear';
      this._apply();
      return this.current;
    } catch (_) { return 'clear'; }
  }

  _apply() {
    if (this.current === 'rain') this._renderRain();
    else if (this.current === 'fog') this._renderFog();
    else if (this.current === 'fireflies') this._renderFireflySwarm();
  }

  _renderRain() {
    const s = this.scene;
    const w = s.scale.width;
    const h = s.scale.height;
    const count = Math.floor((w * h) / 3200);
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const drop = s.add.rectangle(x, y, 1, 10, 0x88aacc, 0.45).setDepth(50);
      this._objects.push(drop);
      const fall = () => {
        drop.x = Math.random() * w;
        drop.y = -20;
        s.tweens.add({
          targets: drop,
          y: h + 20,
          duration: 700 + Math.random() * 500,
          ease: 'Linear',
          onComplete: fall,
        });
      };
      s.time.delayedCall(Math.random() * 2000, fall);
    }
  }

  _renderFog() {
    const s = this.scene;
    const w = s.scale.width;
    const h = s.scale.height;
    // Several large soft circles drifting slowly left to right
    for (let i = 0; i < 6; i++) {
      const startX = -200 - Math.random() * 100;
      const y = h * (0.2 + Math.random() * 0.7);
      const size = 180 + Math.random() * 120;
      const fog = s.add.circle(startX, y, size, 0xd8cce0, 0.12).setDepth(55);
      this._objects.push(fog);
      const drift = () => {
        fog.x = -200 - Math.random() * 100;
        s.tweens.add({
          targets: fog, x: w + 200,
          duration: 30000 + Math.random() * 15000,
          ease: 'Linear',
          onComplete: drift,
        });
      };
      s.time.delayedCall(i * 4000, drift);
    }
  }

  _renderFireflySwarm() {
    const s = this.scene;
    const w = s.scale.width;
    const h = s.scale.height;
    // Dense firefly layer on top of the base scene
    for (let i = 0; i < 40; i++) {
      const x = Math.random() * w;
      const y = h * 0.2 + Math.random() * h * 0.6;
      const ff = s.add.circle(x, y, 1.4 + Math.random() * 1.3, 0xfff4a0, 0.9).setDepth(16);
      const halo = s.add.circle(x, y, 6, 0xfff4a0, 0.3).setDepth(15);
      this._objects.push(ff, halo);
      const drift = () => {
        const tx = x + (Math.random() - 0.5) * 120;
        const ty = y + (Math.random() - 0.5) * 80;
        s.tweens.add({
          targets: [ff, halo], x: tx, y: ty,
          duration: 3000 + Math.random() * 3000, ease: 'Sine.easeInOut',
          onComplete: drift,
        });
      };
      drift();
      s.tweens.add({
        targets: ff, alpha: { from: 0.5, to: 1 },
        duration: 1000 + Math.random() * 1500, yoyo: true, repeat: -1,
      });
      s.tweens.add({
        targets: halo, alpha: { from: 0.15, to: 0.45 }, scale: { from: 0.9, to: 1.5 },
        duration: 1000 + Math.random() * 1500, yoyo: true, repeat: -1,
      });
    }
  }

  cleanup() {
    for (const o of this._objects) { try { o.destroy(); } catch (_) {} }
    this._objects = [];
  }
}
